import './ThreeMonitor.css';
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import * as THREE from 'three';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import SteeringWheel from './images/SteeringWheel.png';
import RETRSCT from './images/retract.png';
import CLEARDAY from './images/clearday.png';
import EXPAND from './images/expand.png';
import SNOW from './images/snow.png';
import RAIN from './images/rain.png';
import DAY from './images/day.png';
import NIGHT from './images/night.png';
import CONSTRUCTION from './images/construction.png';
import ACCUMULATED_WATEWR from './images/accumulated_water.png';
import NODATA from './images/no_data.png';
import { useWorkerStream } from '../worker/useWorkerStream';
import meshDiagnoser from '../../../utils/diagnoser/meshPoolDiagnoser';
import TOP from './images/top.png';
import BUTTOM from './images/bottom.png';
import WebsocketCmp from '../../WebsocketCmp';
import {
  ExtendedCarInfo,
  PackedFrame,
  TrafficLight,
  PlanningTrafficLight,
} from '../utils/types';
import MeshPool from '../utils/MeshPool';
import { GearStateMap } from '../utils/enum';
import { sgmReportCustom } from '@/utils/util';

interface SavedWorkerData {
  createTimestamp: string | undefined;
  dataView: PackedFrame;
  currentTime: number;
  serverTime: string | number;
}

interface ThreeMonitorProps {
  elementId?: string;
  hiddenDelay?: boolean;
  handleRecordDiffTime?: (params: {
    delay: number;
    recordTime: number;
  }) => void;
  updateTrafficLight?: (info: any[]) => void;
  carNumber?: string;
  openData?: any;
  sendData?: any;
  url?: string;
  localMode?: boolean;
}

export interface ThreeMonitorRef {
  feedData: (data: ArrayBuffer) => void;
}

interface LightInfo {
  id: string;
  showLight: boolean;
  duration: number;
  style_text_red: string;
  style_text_green: string;
  style_text_yellow: string;
  trafficLightStateText: string;
}

const TARGET_FPS = 10;
const WORKER_QUEUE_MAX = 20;

const MultiFrameState = {
  NONE: 0,
  FLASHING_GREEN: 1,
  FLASHING_RED: 2,
  FLASHING_YELLOW: 3,
  CONTINUE_UNKNOWN: 4,
  CONTINUE_BLACK: 5,
  CONTINUE_GREEN: 6,
  CONTINUE_RED: 7,
  CONTINUE_YELLOW: 8,
};

const ThreeMonitor = forwardRef<ThreeMonitorRef, ThreeMonitorProps>(
  (props, ref) => {
  const { elementId, hiddenDelay, handleRecordDiffTime } = props;
  // 组件是否已卸载（用于拦截异步回调，避免卸载后仍 add 到 scene 导致泄漏）
  const disposedRef = useRef(false);
  // 相机实例，负责视角与投影
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  // 场景
  const sceneRef = useRef<THREE.Scene | null>(null);
  // 渲染器
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  // DOM 容器，用于挂载 renderer.domElement
  const containerRef = useRef<HTMLElement | null>(null);
  // 控制器（交互）
  const controlsRef = useRef<TrackballControls | null>(null);
  // 场景中车辆模型
  const carMeshRef = useRef<THREE.Group | null>(null);
  // requestAnimationFrame 返回值，用于取消动画循环
  const animationFrameIdRef = useRef<number | null>(null);

  // 车辆朝向四元数（用于把车辆模型旋转到正确方向）
  const quaternionVehicleRef = useRef(new THREE.Quaternion(0, 0, 0, 1));

  // 当前视图中心点（可用于平移镜头）
  const currentOriginXRef = useRef(0);
  const currentOriginYRef = useRef(0);

  // 相机模式标识（跟类组件保持一致）
  const cameraFlagRef = useRef(1);

  // 摄像机缩放相关参考值
  const cameraScaleFollowRef = useRef(30);
  // 顶视默认缩放距离
  const cameraScaleTopRef = useRef(20);
  const cameraLastFlagRef = useRef<number | null>(null);

  // 节流绘制控制器
  const nowRef = useRef(Date.now());
  const thenRef = useRef(Date.now());

  // 右下角的ui显示状态
  const [carGearPosition, setCarGearPosition] = useState('');
  const [textCurrentFrameTime, setTextCurrentFrameTime] = useState('');
  const [showAllLight, setShowAllLight] = useState(false);
  const [showGlobalConfig, setShowGlobalConfig] = useState(true);
  const [globalWeather, setGlobalWeather] = useState(0);
  const [globalTime, setGlobalTime] = useState(0);
  const [globalRegion, setGlobalRegion] = useState(0);
  const [lightInfoList, setLightInfoList] = useState<LightInfo[]>([]);
  const [showImageOcc, setShowImageOcc] = useState(true);

  // 最后处理的帧 ID（用于数据收集）
  const lastProcessedFrameIdRef = useRef<string | number | undefined>(
    undefined
  );

  // 键盘回调函数引用（用于卸载时移除事件监听）
  const keyDownHandlerRef = useRef<((e: KeyboardEvent) => void) | null>(null);

  // MeshPool 引用，以便在卸载时可访问并释放
  const meshPoolRef = useRef<MeshPool | null>(null);

  // websocket引用
  const wsRef = useRef<any>(null);

  // 存储 worker 传回的最新数据，供绘制使用
  const workerDataRef = useRef<SavedWorkerData[]>([]);

  // 更新worker数据的函数
  const updateWorkerFrameDataRef = useRef<((data: any) => void) | null>(null);

  // onWorkerData: worker 解码后回传的数据回调
  // data 结构来源于 worker.postMessage
  // 回调：接收 worker 解码后的消息并将完整帧入队，供后续绘制使用
  const onReceiveWorkerData = useCallback((data: PackedFrame) => {
    // console.log('收到worker回复数据:', data);
    if (!data) return;
    if (data.cmd === 'frame_geo' && data.meta && data.bufs) {
      // 只保留最近几帧，避免堆积导致显示过期帧
      if (workerDataRef.current.length > WORKER_QUEUE_MAX)
        workerDataRef.current.shift();

      const kafkaTimestamp = data.timestamp ?? 0;
      const currentTime = new Date().getTime();
      const dataMsg = {
        // createTimestamp: 消息创建时间（用于展示帧时间），worker 会透传原始 createTimestamp
        createTimestamp: data.createTimestamp,
        dataView: data,
        currentTime,
        serverTime: kafkaTimestamp,
      };
      // 记录主线程收到 worker 解析完成的时间，并将 worker meta 中的时间戳一并传入 diagnoser
      try {
        meshDiagnoser.markMainReceived(
          data.frameId ?? data.createTimestamp ?? data.timestamp,
          data.meta
        );
      } catch (_) {}
      // 入队，后续 DoProcessLocalView 会从 dequeDataviewRef 取出并绘制
      workerDataRef.current.push(dataMsg);
    }
  }, []);

  // 将接收的数据分发给 worker 进行 proto 解码（worker 解码完成后会调用 onWorkerData）
  const { dispatchRawDataToWorker } = useWorkerStream(onReceiveWorkerData);

  // 从 trafficLight 列表中根据 id 查找并返回灯信息
  const GetTrafficLight = useCallback(
    (
      trafficLight: TrafficLight[] | undefined,
      id: string
    ): TrafficLight | null => {
      if (!trafficLight) return null;
      for (let i = 0; i < trafficLight.length; i++) {
        let light = trafficLight[i];
        if (light.id === id) return light;
      }
      console.error('get traffic light failed,id:' + id);
      return null;
    },
    []
  );

  // 生成单个交通灯的可视化信息结构（用于右下角状态 UI）
  const GenerateTrafficLightInfo = useCallback(
    (light: TrafficLight | null): LightInfo | null => {
      if (light === null) return null;
      const id = light.id;
      let trafficLightInfo = {
        id,
        showLight: true,
        duration: 0,
        style_text_red: 'light_red_unknown',
        style_text_green: 'light_green_unknown',
        style_text_yellow: 'light_yellow_unknown',
        trafficLightStateText: '无感知结果',
      };
      if (light.light === 1)
        trafficLightInfo = {
          id,
          duration: Number(light.duration ?? 0),
          showLight: true,
          style_text_red: 'light_red_major',
          style_text_green: 'light_green_unknown',
          style_text_yellow: 'light_yellow_unknown',
          trafficLightStateText: '红灯',
        };
      if (light.light === 2)
        trafficLightInfo = {
          id,
          duration: Number(light.duration ?? 0),
          showLight: true,
          style_text_red: 'light_red_unknown',
          style_text_green: 'light_green_major',
          style_text_yellow: 'light_yellow_unknown',
          trafficLightStateText: '绿灯',
        };
      if (light.light === 3)
        trafficLightInfo = {
          id,
          duration: Number(light.duration ?? 0),
          showLight: true,
          style_text_red: 'light_red_unknown',
          style_text_green: 'light_green_unknown',
          style_text_yellow: 'light_yellow_major',
          trafficLightStateText: '黄灯',
        };
      if (light.light === 4)
        trafficLightInfo = {
          id,
          duration: Number(light.duration ?? 0),
          showLight: true,
          style_text_red: 'light_black',
          style_text_green: 'light_black',
          style_text_yellow: 'light_black',
          trafficLightStateText: '红绿灯不亮',
        };
      if (light.multiFrameState === MultiFrameState.FLASHING_GREEN)
        trafficLightInfo = {
          id,
          duration: Number(light.duration ?? 0),
          showLight: true,
          style_text_red: 'light_red_unknown',
          style_text_green: 'light_green_twinkle',
          style_text_yellow: 'light_yellow_unknown',
          trafficLightStateText: '绿灯闪烁',
        };
      if (light.multiFrameState === MultiFrameState.FLASHING_RED)
        trafficLightInfo = {
          id,
          duration: Number(light.duration ?? 0),
          showLight: true,
          style_text_red: 'light_red_twinkle',
          style_text_green: 'light_green_unknown',
          style_text_yellow: 'light_yellow_unknown',
          trafficLightStateText: '红灯闪烁',
        };
      if (light.multiFrameState === MultiFrameState.FLASHING_YELLOW)
        trafficLightInfo = {
          id,
          duration: Number(light.duration ?? 0),
          showLight: true,
          style_text_red: 'light_red_unknown',
          style_text_green: 'light_green_unknown',
          style_text_yellow: 'light_yellow_twinkle',
          trafficLightStateText: '黄灯闪烁',
        };
      return trafficLightInfo;
    },
    [MultiFrameState]
  );

  // 为关注的交叉口/灯生成兴趣点列表（用于突出显示或高亮）
  const GenerateInterestTrafficLightInfo = useCallback(
    (
      trafficLight: TrafficLight[] | undefined,
      interestedLight: PlanningTrafficLight[] | undefined
    ): LightInfo[] => {
      let lightInfoListLocal = [];
      if (trafficLight && interestedLight && interestedLight.length > 0) {
        for (let i = 0; i < interestedLight.length; i++) {
          let planningTrafficLight = interestedLight[i];
          let id = planningTrafficLight.id;
          let light = GetTrafficLight(trafficLight, id);
          let info = GenerateTrafficLightInfo(light);
          if (info) lightInfoListLocal.push(info);
        }
      }
      return lightInfoListLocal;
    },
    [GetTrafficLight, GenerateTrafficLightInfo]
  );

  // 生成所有路口的交通灯展示信息（用于列表渲染或全览）
  const GenerateAllTrafficLightInfo = useCallback(
    (trafficLight: TrafficLight[] | undefined): LightInfo[] => {
      let lightInfoListLocal: LightInfo[] = [];
      if (trafficLight) {
        for (let i = 0; i < trafficLight.length; i++) {
          let info = GenerateTrafficLightInfo(trafficLight[i]);
          if (info) lightInfoListLocal.push(info);
        }
      }
      return lightInfoListLocal;
    },
    [GenerateTrafficLightInfo]
  );

  // 根据红绿灯列表更新右下角状态面板
  const drawLightList = useCallback(
    (
      trafficLight: TrafficLight[] | undefined,
      interestedLight: PlanningTrafficLight[] | undefined
    ) => {
      if (trafficLight) {
        let info = GenerateInterestTrafficLightInfo(
          trafficLight,
          interestedLight
        );
        if (info.length > 0) {
          setLightInfoList(info);
          props.updateTrafficLight && props.updateTrafficLight(info);
          return;
        }
        info = GenerateAllTrafficLightInfo(trafficLight);
        setLightInfoList(info);
        props.updateTrafficLight && props.updateTrafficLight(info);
      }
    },
    [GenerateInterestTrafficLightInfo, GenerateAllTrafficLightInfo, props]
  );

  // WebSocket open 回调：在连接建立后发送鉴权或订阅请求
  const verifyToken = useCallback(
    (_wsRef: any) => {
      wsRef.current = _wsRef;
      const request = { eventType: 'VEHICLE_DATA', data: props.carNumber };
      let string_request = props.openData
        ? JSON.stringify(props.openData)
        : JSON.stringify(request);
      // guard send
      try {
        wsRef.current.current.send(string_request);
      } catch (e) {}
    },
    [props.carNumber, props.openData]
  );

  // WebSocket close 回调：记录连接关闭状态
  const handleClose = useCallback(() => {
    // connection closed
  }, []);

  // 数据录制相关
  const [isRecording, setIsRecording] = useState(false);
  const isRecordingRef = useRef(false);
  const recordedDataRef = useRef<ArrayBuffer[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 生成二进制文件并下载
  const downloadRecordedData = useCallback(() => {
    const packets = recordedDataRef.current;
    if (packets.length === 0) return;

    let totalLength = 0;
    packets.forEach((buf) => {
      totalLength += 4 + buf.byteLength; // 4字节长度前缀 + 数据
    });

    const result = new Uint8Array(totalLength);
    let offset = 0;

    packets.forEach((buf) => {
      const len = buf.byteLength;
      // 写入长度（小端序 4 字节无符号整数）
      result[offset++] = len & 0xff;
      result[offset++] = (len >> 8) & 0xff;
      result[offset++] = (len >> 16) & 0xff;
      result[offset++] = (len >> 24) & 0xff;
      // 写入数据
      result.set(new Uint8Array(buf), offset);
      offset += len;
    });

    const blob = new Blob([result], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recorded_data_${new Date().getTime()}.bin`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    recordedDataRef.current = [];
  }, []);

  // 开始录制数据（2 分钟后自动停止并下载）
  const startRecording = useCallback(() => {
    if (isRecordingRef.current) return;
    recordedDataRef.current = [];
    isRecordingRef.current = true;
    setIsRecording(true);

    recordingTimerRef.current = setTimeout(
      () => {
        isRecordingRef.current = false;
        setIsRecording(false);
        downloadRecordedData();
        recordingTimerRef.current = null;
      },
      2 * 60 * 1000
    );
  }, [downloadRecordedData]);

  // 停止录制
  const stopRecording = useCallback(() => {
    if (!isRecordingRef.current) return;
    isRecordingRef.current = false;
    setIsRecording(false);
    if (recordingTimerRef.current) {
      clearTimeout(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    downloadRecordedData();
  }, [downloadRecordedData]);

  // 快捷键 Ctrl+Shift+R 切换录制（隐藏按钮，仅限开发调试）
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'R' || e.key === 'r')) {
        e.preventDefault();
        if (isRecordingRef.current) {
          stopRecording();
        } else {
          startRecording();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [startRecording, stopRecording]);

  // WebSocket message 回调：接收二进制帧并转发给 worker 解码
  const handleData = useCallback(
    (data: ArrayBuffer | Blob | any) => {
      const dataType = Object.prototype.toString.call(data);
      if (dataType !== '[object ArrayBuffer]') return;

      // 如果正在录制，保存原始数据
      if (isRecordingRef.current) {
        recordedDataRef.current.push(data.slice(0));
      }

      // 这里的数据已经是arraybuffer了，不需要再转换了
      try {
        // 记录主线程发送数据的 epoch 时间戳，供 diagnoser 计算 ws 到 worker 的延时（跨线程一致性）
        const wsEpoch = Date.now();
        const sendEpoch = Date.now();
        const frameId = dispatchRawDataToWorker(data, sendEpoch);
        if (frameId) meshDiagnoser.pushOutbound(frameId, sendEpoch, wsEpoch);
      } catch (_) {}
    },
    [dispatchRawDataToWorker]
  );

  // 更新控制目标（可能用于摄像机跟随/对齐逻辑）
  const updateControlTarget = useCallback(() => {
    let eyesight: THREE.Vector3;
    const controls = controlsRef.current;
    if (!controls) return;
    // 通过“目标点前移”来控制车辆在屏幕中的垂直位置：
    // - 顶视(flag=1)：目标点沿车头方向前移，让车辆落在画布中间偏下（约底部 1/3）
    // - 其它视角：保持居中，避免驾驶/跟随视角手感变化
    let car_offset = new THREE.Vector3(4, 0, 0);
    car_offset.applyQuaternion(quaternionVehicleRef.current);
    let car_pos = new THREE.Vector3(
      currentOriginXRef.current,
      currentOriginYRef.current,
      0
    );
    let car_shadow_pos = new THREE.Vector3(0, 0, 0);
    car_shadow_pos.addVectors(car_pos, car_offset);
    if (cameraFlagRef.current === 0 || cameraFlagRef.current === 1) {
      if (cameraFlagRef.current === 0) eyesight = new THREE.Vector3(-1, 0, 0.5);
      else eyesight = new THREE.Vector3(-0.00001, 0, 1);
      eyesight.applyQuaternion(quaternionVehicleRef.current);
      eyesight.normalize();
      let fscale;
      if (cameraLastFlagRef.current !== cameraFlagRef.current) {
        if (cameraFlagRef.current === 0) fscale = cameraScaleFollowRef.current;
        else fscale = cameraScaleTopRef.current;
      } else {
        fscale = controls.object.position.z / eyesight.z;
        if (cameraFlagRef.current === 0) cameraScaleFollowRef.current = fscale;
        else cameraScaleTopRef.current = fscale;
      }
      eyesight.multiplyScalar(fscale);
      let target = car_shadow_pos;
      let pos = new THREE.Vector3(0, 0, 0);
      pos.addVectors(target, eyesight);
      controls.object.position.set(pos.x, pos.y, pos.z);
      controls.target = target;
      controls.object.up.set(0, 0, 1);
      cameraLastFlagRef.current = cameraFlagRef.current;
    } else if (cameraFlagRef.current === 2) {
      // 进入该视角也更新 lastFlag，便于后续切换回其它视角时按“切换”逻辑恢复缩放。
      cameraLastFlagRef.current = cameraFlagRef.current;
      eyesight = new THREE.Vector3(-5, 0, 1);
      eyesight.applyQuaternion(quaternionVehicleRef.current);
      eyesight.normalize();
      let fscale = 10;
      eyesight.setX(-eyesight.x);
      eyesight.setY(-eyesight.y);
      eyesight.setZ(-eyesight.z);
      let car_location_offset = new THREE.Vector3(
        eyesight.x,
        eyesight.y,
        eyesight.z
      );
      eyesight.multiplyScalar(fscale);
      let car_location = new THREE.Vector3(
        currentOriginXRef.current,
        currentOriginYRef.current,
        1.5
      );
      let pos = new THREE.Vector3(0, 0, 0);
      pos.addVectors(car_location, eyesight);
      controls.object.position.set(
        currentOriginXRef.current + car_location_offset.x,
        currentOriginYRef.current + car_location_offset.y,
        1.5
      );
      controls.target = pos;
      controls.object.up.set(0, 0, 1);
    }
  }, []);

  // 切换是否显示所有红绿灯（供调试/展示使用）
  const changeShowAllLight = useCallback(() => {
    setShowAllLight((v) => !v);
  }, []);

  // 更新 MCP（地图控制点或元信息）在场景中的显示
  const updateViewMcp = useCallback((mcp_data: any) => {
    if (!mcp_data) return;
    const global_weather = mcp_data.scWeatherType;
    const global_time = mcp_data.scTimeType;
    const global_region = mcp_data.scRegionType;
    setGlobalWeather(global_weather);
    setGlobalTime(global_time);
    setGlobalRegion(global_region);
  }, []);

  // 切换全局配置面板的显示状态
  const update_show_global_config = useCallback(() => {
    setShowGlobalConfig((s) => !s);
  }, []);

  const drawVehicleLocation = useCallback((carData?: ExtendedCarInfo) => {
    try {
      const { vehiclePositionOffset, origin, orientation } =
        carData || ({} as ExtendedCarInfo);

      const offsetX = vehiclePositionOffset?.x ?? 0;
      const offsetY = vehiclePositionOffset?.y ?? 0;
      const originX = origin?.x ?? 0;
      const originY = origin?.y ?? 0;

      // 无论车辆模型是否已加载，都更新四元数，供相机跟随计算使用。
      const yaw = (orientation as any)?.z ?? (orientation as any)?.yaw ?? 0;
      quaternionVehicleRef?.current?.setFromEuler(new THREE.Euler(0, 0, yaw));

      if (carMeshRef.current) {
        // 设置位置（安全取值）
        const realPositionX = originX + offsetX;
        const realPositionY = originY + offsetY;
        carMeshRef.current.position.x = realPositionX;
        carMeshRef.current.position.y = realPositionY;

        // 设置姿态
        carMeshRef.current.setRotationFromQuaternion(
          quaternionVehicleRef.current
        );
      }

      // 更新全局的原点位置（用于坐标变换/其他对象定位）
      currentOriginXRef.current = originX;
      currentOriginYRef.current = originY;
    } catch (_) {}
  }, []);

  // TODO-主绘制入口
  const drawDataView = useCallback(
    (localview: PackedFrame) => {
      // 自车位置
      drawVehicleLocation(localview.meta.carInfo);

      // 右下角的红绿灯面板（保持原有的写法，后续继续优化）
      const trafficLights = localview.meta.trafficLight || [];
      const interested = localview.meta.planningInterestedLights || [];
      drawLightList(trafficLights, interested);

      // 更新 MCP（全局感知如天气/时间/区域），供 UI 面板显示
      try {
        if (localview.meta && localview.meta.mcp)
          updateViewMcp(localview.meta.mcp);
      } catch (_) {}

      // 更新挡位信息
      try {
        if (localview?.meta?.carInfo?.gearPosition != null) {
          const gearPos = localview.meta.carInfo.gearPosition;
          const text = GearStateMap[gearPos] || '';
          setCarGearPosition(text);
        }
      } catch (_) {}

      // 控制器
      try {
        updateControlTarget();
      } catch (_) {}

      // meshPool处理所有点线面元素
      try {
        if (updateWorkerFrameDataRef.current)
          updateWorkerFrameDataRef.current(localview);
      } catch (_) {}
    },
    [drawLightList, updateControlTarget]
  );

  /**
   * 拿出已经存在的缓存数据，开始绘制
   */
  const doProcessLocalView = useCallback((): boolean => {
    if (workerDataRef.current.length > 0) {
      const data = workerDataRef.current.shift();
      if (!data) return false;
      // console.log('取出数据进行处理====', data);

      // 更新当前帧的时间
      const createTsRaw = data.createTimestamp ?? 0;
      const createTsNum = Number(createTsRaw) || 0;
      const diffSecs = createTsNum
        ? (data.currentTime - createTsNum / 1000000) / 1000
        : 0;
      const timeText =
        (createTsNum ? new Date(createTsNum / 1000000).toLocaleString() : '—') +
        (!hiddenDelay ? ' 延时:' + diffSecs.toFixed(3) + '秒' : '');
      setTextCurrentFrameTime(timeText);

      // 标记当前处理的 frame id，优先使用 worker 回显的 `frameId`，供渲染完成时打点
      lastProcessedFrameIdRef.current =
        data.dataView?.frameId ??
        data.createTimestamp ??
        data.serverTime ??
        data.dataView?.createTimestamp;

      // 绘制数据
      drawDataView(data.dataView);

      // 对外提供当前帧的时间信息
      handleRecordDiffTime &&
        handleRecordDiffTime({
          delay: parseFloat(diffSecs?.toFixed(3)) * 1000,
          recordTime: data.currentTime,
        });

      return true;
    }

    return false;
  }, [handleRecordDiffTime, hiddenDelay, drawDataView]);

  // 主动画循环（双时钟）：
  // - 每帧：controls.update + renderer.render（交互丝滑）
  // - 限频：doProcessLocalView（几何/数据更新，避免主线程被重活拖垮）
  const animation = useCallback(() => {
    animationFrameIdRef.current = requestAnimationFrame(animation);
    const interval = 1000 / TARGET_FPS;
    nowRef.current = Date.now();
    const delta = nowRef.current - thenRef.current;

    let geometryUpdated = false;
    if (delta > interval) {
      thenRef.current = nowRef.current - (delta % interval);
      geometryUpdated = doProcessLocalView();
    }

    if (controlsRef.current) controlsRef.current.update();
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      // 在 render 前做屏幕空间标签布局，避免重叠（MeshPool 内部实现）
      try {
        if (meshPoolRef.current && cameraRef.current && rendererRef.current) {
          // @ts-ignore 调用我们动态添加的方法
          meshPoolRef.current.layoutTrafficLightLabels(
            cameraRef.current,
            rendererRef.current
          );
        }
      } catch (_) {}

      rendererRef.current.render(sceneRef.current, cameraRef.current);

      // 只在“本 tick 确实更新了几何/切换了帧”时打点，避免每帧 render 都重复记同一帧。
      if (geometryUpdated) {
        try {
          meshDiagnoser.markRenderDisplayed(lastProcessedFrameIdRef.current);
        } catch (_) {}
      }
    }
  }, [doProcessLocalView]);

  const initMeshPool = useCallback(() => {
    if (!sceneRef.current) return;
    const meshPool = new MeshPool(sceneRef.current);
    meshPoolRef.current = meshPool;
    updateWorkerFrameDataRef.current = (data) =>
      meshPool.processPackedFrame(data);
  }, []);

  // 初始化全局监听器：注册键盘事件等
  const initListener = useCallback(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'o' || event.key === 'O') {
        setShowImageOcc((v) => !v);
      }
    };
    keyDownHandlerRef.current = handler;
    document.addEventListener('keydown', handler);
  }, []);

  /**
   * 卸载材质
   */
  const disposeMaterial = useCallback((material: any) => {
    if (!material) return;
    try {
      if (typeof material.dispose === 'function') material.dispose();
    } catch (_) {}
    try {
      for (const key of Object.keys(material)) {
        const value = material[key];
        if (value && typeof value === 'object' && value.isTexture) {
          try {
            value.dispose();
          } catch (_) {}
        }
      }
    } catch (_) {}
  }, []);

  /**
   * 统一释放 Object3D 子树下的几何/材质/纹理
   */
  const disposeObject3D = useCallback(
    (root?: THREE.Object3D | null) => {
      if (!root) return;
      try {
        root.traverse((object: any) => {
          if (!object) return;
          // 仅处理包含 GPU 资源的对象
          if (
            !object.isMesh &&
            !object.isLine &&
            !object.isPoints &&
            !object.isSprite
          )
            return;

          try {
            object.geometry?.dispose?.();
          } catch (_) {}

          try {
            const mat = object.material;
            if (!mat) return;
            if (Array.isArray(mat)) mat.forEach((m: any) => disposeMaterial(m));
            else disposeMaterial(mat);
          } catch (_) {}
        });
      } catch (_) {}
    },
    [disposeMaterial]
  );

  /**
   * 卸载函数
   */
  const dispose = useCallback(() => {
    if (disposedRef.current) return;
    disposedRef.current = true;

    animationFrameIdRef.current &&
      cancelAnimationFrame(animationFrameIdRef.current);
    animationFrameIdRef.current = null;

    if (keyDownHandlerRef.current) {
      document.removeEventListener('keydown', keyDownHandlerRef.current);
      keyDownHandlerRef.current = null;
    }

    controlsRef.current?.dispose?.();
    controlsRef.current = null;

    // 先释放 MeshPool（内部会从 scene 中移除自己管理的对象，并释放 TrafficLightLabelPool）
    meshPoolRef.current?.dispose?.();
    meshPoolRef.current = null;

    // 释放车模
    if (carMeshRef.current) {
      carMeshRef.current.parent?.remove?.(carMeshRef.current);
      disposeObject3D(carMeshRef.current);
      carMeshRef.current = null;
    }

    // 释放场景中剩余对象（灯光等）
    const scene = sceneRef.current;
    if (scene) {
      disposeObject3D(scene);
      // @ts-ignore
      scene.clear?.();
    }

    // 释放渲染器
    if (rendererRef.current) {
      rendererRef.current.renderLists?.dispose?.();
      rendererRef.current.dispose();
      rendererRef.current.forceContextLoss?.();
      try {
        if (containerRef.current && rendererRef.current.domElement) {
          containerRef.current.removeChild(rendererRef.current.domElement);
        }
      } catch (_) {}
      rendererRef.current = null;
    }

    // 关闭 WebSocket
    wsRef.current?.current?.close?.();
    wsRef.current = null;

    // 清空剩余引用
    workerDataRef.current = [];
    updateWorkerFrameDataRef.current = null;
    cameraRef.current = null;
    sceneRef.current = null;
    containerRef.current = null;
  }, [disposeObject3D]);

  // 初始化 Three.js 场景、相机、灯光和载入初始模型
  const initThree = useCallback(() => {
    // 如果组件发生过卸载（理论上不会再 init），这里也保证状态一致
    disposedRef.current = false;
    let scene_ = new THREE.Scene();
    sceneRef.current = scene_;
    containerRef.current = document.getElementById(
      elementId || 'WebGL-output'
    ) as HTMLElement | null;
    let width = containerRef.current?.clientWidth ?? window.innerWidth;
    let height = containerRef.current?.clientHeight ?? window.innerHeight - 60;

    // 相机：PerspectiveCamera，用于场景视角（跟随/顶视切换由 cameraFlag 控制）
    const camera_ = new THREE.PerspectiveCamera(60, width / height, 1, 2000);
    camera_.position.x = currentOriginXRef.current;
    camera_.position.y = currentOriginYRef.current;
    // 初始化时直接按当前视角的默认缩放设置 z，避免首帧到来前视野过大
    camera_.position.z =
      cameraFlagRef.current === 0
        ? cameraScaleFollowRef.current
        : cameraScaleTopRef.current;
    camera_.lookAt(
      new THREE.Vector3(currentOriginXRef.current, currentOriginYRef.current, 0)
    );
    cameraRef.current = camera_;

    // 交互控件：TrackballControls（用于平移/缩放/旋转场景）
    controlsRef.current = new TrackballControls(
      camera_,
      (containerRef.current as HTMLElement) ?? undefined
    );
    if (controlsRef.current) {
      controlsRef.current.minDistance = 10;
      controlsRef.current.maxDistance = 1000;
    }

    // 灯光：AmbientLight（环境光） + DirectionalLight（主光源），影响 Mesh (MeshLambert/Phong 等)
    let amBi = new THREE.AmbientLight(0xffffff);
    scene_.add(amBi);
    const spotLight = new THREE.DirectionalLight(0xffffff);
    spotLight.position.set(550, 100, 50);
    spotLight.intensity = 0.8;
    scene_.add(spotLight);

    // 模型加载：加载车辆 GLTF 模型，作为场景内的 Mesh（carMeshRef）用于展示车辆姿态
    const loader = new GLTFLoader().setPath('/models/');
    try {
      loader.load('car01.gltf', (gltf) => {
        try {
          const car_mesh = (gltf &&
            (gltf.scene || (gltf.scenes && gltf.scenes[0]))) as
            | THREE.Group
            | THREE.Object3D
            | undefined;
          if (!car_mesh) {
            console.warn('load car01.gltf failed: gltf.scene is empty');
            return;
          }

          // 卸载后仍可能收到异步回调：直接释放，不再挂到 scene/refs 上
          if (disposedRef.current) {
            disposeObject3D(car_mesh);
            return;
          }

          (car_mesh as any).scale?.set?.(0.01, 0.01, 0.01);
          car_mesh.position.set(0, 0, 0);
          scene_.add(car_mesh);
          carMeshRef.current = car_mesh as THREE.Group;
        } catch (e) {
          console.warn('GLTF load callback failed', e);
        }
      });
    } catch (e) {
      console.warn('GLTF loader failed to start', e);
    }

    // 渲染器：WebGLRenderer，负责将 Scene/Camera 渲染到 canvas
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setClearColor(0x050505);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    try {
      if (containerRef.current)
        containerRef.current.appendChild(renderer.domElement);
    } catch (e) {
      console.warn('append renderer dom failed', e);
    }
    rendererRef.current = renderer;
  }, [elementId, disposeObject3D]);

  /**
   * 主入口
   */
  useEffect(() => {
    initThree();
    initMeshPool();
    animation();
    initListener();
    return () => {
      try {
        dispose();
      } catch (_) {}
    };
  }, []);

  /**
   * 性能统计查看：双击右下角的时间显示区域会打印最近 3s 的各阶段平均耗时（ws->send, ws->workerRecv, workerRecv->mainRecv, mainRecv->render, end2end）
   */
  const handleDriveModeDoubleClick = useCallback(() => {
    try {
      const stats = meshDiagnoser.getAverages(3000);
      sgmReportCustom(1, '3d数据采集', JSON.stringify(stats));
    } catch (e) {
      console.error(e);
    }
  }, []);

  useImperativeHandle(ref, () => ({
    feedData: (data: ArrayBuffer) => {
      handleData(data);
    },
  }));

  return (
    <div id={elementId || 'WebGL-output'}>
      <div className="timelabel_text">{textCurrentFrameTime}</div>
      {isRecording && (
        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff4d4f' }} />
          <span style={{ color: '#ff4d4f', fontSize: 12 }}>REC</span>
        </div>
      )}
      {!props.localMode && (
        <WebsocketCmp
          url={props.url || ''}
          onOpen={verifyToken}
          onMessage={handleData}
          onClose={handleClose}
          socketBinaryType="arraybuffer"
        />
      )}
      <div className="global_config">
        <div className="contral_icon" onClick={update_show_global_config}>
          <img src={showGlobalConfig ? RETRSCT : EXPAND} alt="收起更多" />
        </div>
        <div
          className="global_config_data"
          style={{ display: showGlobalConfig ? 'block' : 'none' }}
        >
          <div className="config_data_item">
            <div className="config_item_icon">
              <img
                src={
                  globalWeather === 0
                    ? NODATA
                    : globalWeather === 1
                      ? RAIN
                      : globalWeather === 2
                        ? CLEARDAY
                        : SNOW
                }
                alt="icon"
              />
            </div>
            <div className="config_item_text">
              {globalWeather === 0
                ? '天气'
                : globalWeather === 1
                  ? '雨'
                  : globalWeather === 2
                    ? '晴'
                    : '雪'}
            </div>
          </div>
          <div className="config_data_item">
            <div className="config_item_icon">
              <img
                src={globalTime === 0 ? NODATA : globalTime === 1 ? DAY : NIGHT}
                alt="icon"
              />
            </div>
            <div className="config_item_text">
              {globalTime === 0 ? '时间' : globalTime === 1 ? '白天' : '黑夜'}
            </div>
          </div>
          <div className="config_data_item">
            <div className="config_item_icon">
              <img
                src={
                  globalRegion === 0
                    ? NODATA
                    : globalRegion === 1
                      ? CONSTRUCTION
                      : ACCUMULATED_WATEWR
                }
                alt="icon"
              />
            </div>
            <div className="config_item_text">
              {globalRegion === 0
                ? '路面'
                : globalRegion === 1
                  ? '施工'
                  : '积水'}
            </div>
          </div>
        </div>
      </div>
      <div className="CarInfo">
        <div className="light_button" onClick={changeShowAllLight}>
          {showAllLight ? (
            <img src={BUTTOM} alt="收起" />
          ) : (
            <img src={TOP} alt="展开" />
          )}
        </div>
        <div className="lightList">
          {lightInfoList.length > 0 ? (
            showAllLight ? (
              lightInfoList.map((item, index) => (
                <div key={index} className="light_container">
                  <div className="light_information">
                    <span className="light_id">{item.id}</span>
                    <span className="light_duration">
                      {item?.duration?.toFixed(2)}
                    </span>
                  </div>
                  <div className="light_stateText">
                    {item.trafficLightStateText}
                  </div>
                  <div className="light">
                    <div className={item.style_text_red} />
                    <div className={item.style_text_green} />
                    <div className={item.style_text_yellow} />
                  </div>
                </div>
              ))
            ) : (
              <div className="light_container">
                <div className="light_information">
                  <span className="light_id">{lightInfoList[0].id}</span>
                  <span className="light_duration">
                    {lightInfoList?.[0]?.duration?.toFixed(2)}
                  </span>
                </div>
                <div className="light_stateText">
                  {lightInfoList[0].trafficLightStateText}
                </div>
                <div className="light">
                  <div className={lightInfoList[0].style_text_red} />
                  <div className={lightInfoList[0].style_text_green} />
                  <div className={lightInfoList[0].style_text_yellow} />
                </div>
              </div>
            )
          ) : (
            <div className="light_container">
              <div className="light_none">
                <div />
                <div />
                <div />
              </div>
            </div>
          )}
        </div>
        <div className="car_info" onDoubleClick={handleDriveModeDoubleClick}>
          <div className="car_img">
            <img src={SteeringWheel} alt="机车方向盘" />
          </div>
          <div className="car_gear_text">{carGearPosition}</div>
        </div>
      </div>
    </div>
  );
});

export default ThreeMonitor;
